// usePushNotifications.js
// Registers SW, asks permission, subscribes, sends to backend.
// Call once after login: usePushNotifications(user)
// Android: same VAPID flow works on Chrome Android. Native FCM = separate step later.

import { useEffect } from "react";

const API          = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/$/, "");
const STORAGE_KEY  = "push_asked";        // prevent re-prompting
const VAPID_PUBLIC = process.env.REACT_APP_VAPID_PUBLIC_KEY || "";

function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64     = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw     = window.atob(b64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

async function registerAndSubscribe(userId) {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
  if (!VAPID_PUBLIC) return;

  // Register SW
  let reg;
  try {
    reg = await navigator.serviceWorker.register("/sw.js");
  } catch {
    return;
  }

  // Check current permission
  const perm = await Notification.requestPermission();
  if (perm !== "granted") return;

  // Already subscribed?
  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    // Re-send to backend in case it was lost
    await sendToBackend(userId, existing);
    return;
  }

  // Subscribe
  try {
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly:      true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC),
    });
    await sendToBackend(userId, sub);
  } catch {
    // User denied or browser blocked — silently ignore
  }
}

async function sendToBackend(userId, subscription) {
  try {
    await fetch(`${API}/notifications/subscribe`, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ user_id: userId, subscription: subscription.toJSON() }),
    });
  } catch {
    // Network error — non-fatal
  }
}

export default function usePushNotifications(user) {
  useEffect(() => {
    if (!user?.id) return;
    // Only ask once per browser session (not once-ever — subscription can expire)
    const alreadyAsked = sessionStorage.getItem(STORAGE_KEY);
    if (alreadyAsked) return;
    sessionStorage.setItem(STORAGE_KEY, "1");
    registerAndSubscribe(user.id);
  }, [user?.id]);
}
